const API_BASE = import.meta.env.VITE_API_URL || '/api';

let getToken: (() => Promise<string>) | null = null;

/** Called once by App to wire the token getter */
export function setTokenGetter(fn: () => Promise<string>) {
  getToken = fn;
}

async function request<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string>),
  };

  if (getToken) {
    try {
      headers['Authorization'] = `Bearer ${await getToken()}`;
    } catch {
      // Not authenticated — continue without token
    }
  }

  const res = await fetch(`${API_BASE}${path}`, { ...opts, headers });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = body?.error?.message || `Request failed: ${res.status}`;
    throw new ApiError(message, res.status, body?.error?.code);
  }

  return res.json();
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── Types (lightweight client-side mirrors) ─────────────────
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

export interface Board {
  id: string;
  name: string;
  description?: string;
  columns: Column[];
  settings: {
    autoAssignments?: boolean;
    requiredApprovals?: number;
    defaultTemplate?: string;
    runner?: { cliCommand?: string; autoStart?: boolean; claudeEnv?: Record<string, string> };
    integrations?: Record<string, unknown>;
    notifications?: Record<string, unknown>;
    color?: string;
    [key: string]: unknown;
  };
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface Column {
  id: string;
  boardId: string;
  name: string;
  description?: string;
  order: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export type QuestionStatus = 'open' | 'answered';

export interface AgentQuestion {
  id: string;
  agentId: string;
  phase: string;
  question: string;
  context?: string;
  answer?: string;
  answeredBy?: string;
  answeredAt?: string;
  status: QuestionStatus;
  createdAt: string;
}

export interface CardAttachment {
  id: string;
  s3Key: string;
  filename: string;
  contentType: string;
  size: number;
  uploadedAt: string;
  uploadedBy: string;
}

export type PhaseActionName = 'createPR' | 'autoMergePR' | 'trackDeployment';

export interface PhaseActionError {
  phase: string;
  message: string;
  code?: string;
  timestamp: string;
}

export interface RetryActionResponse {
  success: boolean;
  action: PhaseActionName;
  prNumber?: number;
  prUrl?: string;
  mergeSha?: string;
  message?: string;
  error?: { code: string; message: string };
}

export interface Card {
  id: string;
  boardId: string;
  columnId: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  order: number;
  assignedAgents: string[];
  metadata: {
    tags: string[];
    githubBranch?: string;
    githubPR?: string;
    estimatedHours?: number;
    actualHours?: number;
    questions?: AgentQuestion[];
    inputRequired?: boolean;
    needsReeval?: boolean;
    reevalReason?: string;
    lastSessionId?: string;
    plan?: string;
    prUrl?: string;
    prMerged?: boolean;
    prMergeCommitSha?: string;
    deploymentStatus?: string;
    claim?: { runnerId: string; claimedAt: string; expiresAt: string; phase: string };
    planS3Key?: string;
    triageNotesS3Key?: string;
    attachments?: CardAttachment[];
    phaseActionErrors?: Partial<Record<PhaseActionName, PhaseActionError>>;
    prHistory?: Array<{
      number?: number;
      url?: string;
      branch?: string;
      merged?: boolean;
      archivedAt: string;
    }>;
    blockedBy?: string[];
  };
  questionsCount?: number;  // Summary-only: open question count
  planUrl?: string;         // Pre-signed S3 URL for plan content
  triageNotesUrl?: string;  // Pre-signed S3 URL for triage notes
  attachmentUrls?: Record<string, string>;  // attachmentId → presigned GET URL
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface Activity {
  id: string;
  cardId: string;
  boardId?: string;
  agentId: string;
  type: string;
  phase: string;
  title: string;
  description: string;
  metadata: Record<string, unknown>;
  contentUrl?: string;  // Pre-signed S3 URL for full output
  createdAt: string;
}

export type AgentType = string;

export type Phase = string;

export interface AgentSettings {
  model: string;
  temperature: number;
  maxRetries: number;
  tools: string[];
  contextWindow: number;
  effort?: 'low' | 'medium' | 'high';
  maxBudgetUsd?: number;
}

export interface EscalationRule {
  condition: string;
  action: string;
  threshold: number;
  recipients: string[];
}

export interface TemplateSettings {
  maxConcurrentRuns: number;
  autoRetry: boolean;
  retryCount: number;
  escalationRules: EscalationRule[];
}

export interface AgentRole {
  id: string;
  name: string;
  type: string;
  systemPrompt: string;
  capabilities: string[];
  phases: string[];
  settings: AgentSettings;
}

export interface WorkflowPhase {
  name: string;
  description: string;
  agents: string[];
  autoAdvance: boolean;
  timeoutMinutes?: number;
  successCriteria: string[];
  phasePrompt?: string;
  color?: string;
  createPR?: boolean;
  autoMergePR?: boolean;
  trackDeployment?: boolean;
  runCliCommand?: boolean;
  cliCommand?: string;
  disabled?: boolean;
  permissionMode?: 'plan' | 'default';
}

export interface TeamTemplate {
  id: string;
  name: string;
  description: string;
  version: string;
  agents: AgentRole[];
  workflow: WorkflowPhase[];
  settings: TemplateSettings;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface TemplateRevision {
  templateId: string;
  version: string;
  snapshot?: TeamTemplate;
  createdAt: string;
  author: string;
}

// ─── Auth endpoints ───────────────────────────────────────────
export const auth = {
  me: () => request<ApiResponse<{ sub: string; email: string; isAdmin: boolean }>>('/auth/me'),
};

// ─── Log types + endpoints ────────────────────────────────────
export interface LogEntry {
  id: string;
  timestamp: number;
  level: 'info' | 'warn' | 'error';
  message: string;
}

export const logs = {
  list: (params?: { since?: number; limit?: number; level?: string }) => {
    const qs = new URLSearchParams();
    if (params?.since) qs.set('since', String(params.since));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.level) qs.set('level', params.level);
    const query = qs.toString();
    return request<ApiResponse<LogEntry[]>>(`/logs${query ? `?${query}` : ''}`);
  },
};

// ─── Board endpoints ─────────────────────────────────────────
export const boards = {
  list: () => request<ApiResponse<Board[]>>('/boards'),
  get: (id: string) => request<ApiResponse<Board>>(`/boards/${id}`),
  create: (data: { name: string; description?: string; templateId: string }) =>
    request<ApiResponse<Board>>('/boards', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Board>) =>
    request<ApiResponse<Board>>(`/boards/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<ApiResponse<void>>(`/boards/${id}`, { method: 'DELETE' }),
  changeTemplate: (id: string, templateId: string) =>
    request<ApiResponse<Board>>(`/boards/${id}/change-template`, {
      method: 'PUT',
      body: JSON.stringify({ templateId }),
    }),
};

// ─── Card endpoints ──────────────────────────────────────────
export const cards = {
  list: (boardId: string) =>
    request<ApiResponse<Card[]>>(`/boards/${boardId}/cards?summary=true`),
  listByColumn: (
    boardId: string,
    columnId: string,
    opts?: { cursor?: string; limit?: number; direction?: 'asc' | 'desc' },
  ) => {
    const params = new URLSearchParams({ summary: 'true', columnId });
    if (opts?.cursor) params.set('cursor', opts.cursor);
    if (opts?.limit != null) params.set('limit', String(opts.limit));
    if (opts?.direction) params.set('direction', opts.direction);
    return request<ApiResponse<Card[]>>(`/boards/${boardId}/cards?${params.toString()}`);
  },
  get: (boardId: string, cardId: string) =>
    request<ApiResponse<Card>>(`/boards/${boardId}/cards/${cardId}`),
  create: (boardId: string, data: { title?: string; description?: string; columnId?: string }) =>
    request<ApiResponse<Card>>(`/boards/${boardId}/cards`, { method: 'POST', body: JSON.stringify(data) }),
  update: (boardId: string, cardId: string, data: Partial<Card>) =>
    request<ApiResponse<Card>>(`/boards/${boardId}/cards/${cardId}`, { method: 'PUT', body: JSON.stringify(data) }),
  move: (boardId: string, cardId: string, data: { columnId: string; order?: number }) =>
    request<ApiResponse<Card>>(`/boards/${boardId}/cards/${cardId}/move`, { method: 'PUT', body: JSON.stringify(data) }),
  activities: (boardId: string, cardId: string) =>
    request<ApiResponse<Activity[]>>(`/boards/${boardId}/cards/${cardId}/activities`),
  answerQuestion: (boardId: string, cardId: string, questionId: string, answer: string) =>
    request<ApiResponse<AgentQuestion>>(`/boards/${boardId}/cards/${cardId}/questions/${questionId}/answer`, {
      method: 'PUT',
      body: JSON.stringify({ answer }),
    }),
  acknowledgeReeval: (boardId: string, cardId: string) =>
    request<ApiResponse<Card>>(`/boards/${boardId}/cards/${cardId}/acknowledge-reeval`, {
      method: 'PUT',
    }),
  delete: (boardId: string, cardId: string) =>
    request<ApiResponse<void>>(`/boards/${boardId}/cards/${cardId}`, { method: 'DELETE' }),
  cancelWork: (boardId: string, cardId: string) =>
    request<ApiResponse<Card>>(`/boards/${boardId}/cards/${cardId}/cancel-work`, { method: 'POST' }),
  submitPrompt: (boardId: string, cardId: string, data: { prompt: string; agentRoleId: string }) =>
    request<ApiResponse<{ promptId: string }>>(`/boards/${boardId}/cards/${cardId}/prompt`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  retryAction: (boardId: string, cardId: string, action: PhaseActionName) =>
    request<RetryActionResponse>(`/boards/${boardId}/cards/${cardId}/retry-action`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),
  boardActivities: (boardId: string, limit = 100) =>
    request<ApiResponse<Activity[]>>(`/boards/${boardId}/activities?limit=${limit}`),
  attachmentUploadUrl: (
    boardId: string,
    cardId: string,
    data: { filename: string; contentType: string; size: number },
  ) =>
    request<ApiResponse<{ uploadUrl: string; s3Key: string; attachmentId: string; filename: string }>>(
      `/boards/${boardId}/cards/${cardId}/attachment-upload-url`,
      { method: 'POST', body: JSON.stringify(data) }
    ),
};

// ─── GitHub endpoints ────────────────────────────────────────
export interface GitHubStatus {
  connected: boolean;
  username?: string;      // legacy compat (first token)
  avatarUrl?: string;     // legacy compat
  tokens?: Array<{ id: string; label: string; username: string; avatarUrl: string }>;
}
export interface GitHubRepo { owner: string; name: string; fullName: string; private: boolean; defaultBranch: string; }
export interface GitHubRepoGroup {
  tokenId: string;
  label: string;
  username: string;
  repos: GitHubRepo[];
  error?: string;
}

export const github = {
  status: () => request<ApiResponse<GitHubStatus>>('/github/status'),
  connect: (token: string, label: string) =>
    request<ApiResponse<GitHubStatus>>('/github/connect', { method: 'PUT', body: JSON.stringify({ token, label }) }),
  disconnect: (tokenId?: string) =>
    request<ApiResponse<GitHubStatus>>(`/github/disconnect${tokenId ? `?tokenId=${tokenId}` : ''}`, { method: 'DELETE' }),
  repos: () => request<ApiResponse<GitHubRepoGroup[]>>('/github/repos'),
  boardSettings: {
    get: (boardId: string) => request<ApiResponse<any>>(`/github/boards/${boardId}/settings`),
    set: (boardId: string, data: { owner: string; repo: string; tokenId?: string }) =>
      request<ApiResponse<any>>(`/github/boards/${boardId}/settings`, { method: 'PUT', body: JSON.stringify(data) }),
  },
};

// ─── Agent Run endpoints ──────────────────────────────────────
export interface AgentRunData {
  id: string;
  cardId: string;
  agentRoleId: string;
  phase: string;
  status: string;
  startedAt: string;
  completedAt?: string;
  usage: { tokens: { input: number; output: number; total: number }; costUSD: number; timeMinutes: number };
  output?: { summary: string };
  error?: { type: string; message: string };
}

export const agentRuns = {
  list: (params?: { status?: string; cardId?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.cardId) qs.set('cardId', params.cardId);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<ApiResponse<AgentRunData[]>>(`/agents/runs${query ? `?${query}` : ''}`);
  },
  get: (runId: string, cardId: string) =>
    request<ApiResponse<AgentRunData>>(`/agents/runs/${runId}?cardId=${encodeURIComponent(cardId)}`),
  cancel: (runId: string, cardId: string) =>
    request<ApiResponse<AgentRunData>>(`/agents/runs/${runId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ cardId }),
    }),
  listByCard: (cardId: string, limit = 50) =>
    request<ApiResponse<AgentRunData[]>>(`/agents/runs?cardId=${encodeURIComponent(cardId)}&limit=${limit}`),
  status: () =>
    request<ApiResponse<any>>('/agents/status'),
};

// ─── Runner endpoints ─────────────────────────────────────────
export const runner = {
  command: (boardId: string, action: 'start' | 'stop') =>
    request<{ success: boolean; queued: boolean }>(`/boards/${boardId}/runner`, {
      method: 'PUT',
      body: JSON.stringify({ action }),
    }),
};

// ─── Template endpoints ──────────────────────────────────────
export const templates = {
  list: () => request<ApiResponse<TeamTemplate[]>>('/templates'),
  get: (id: string) => request<ApiResponse<TeamTemplate>>(`/templates/${id}`),
  create: (data: Partial<TeamTemplate>) =>
    request<ApiResponse<TeamTemplate>>('/templates', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<TeamTemplate>) =>
    request<ApiResponse<TeamTemplate>>(`/templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<ApiResponse<void>>(`/templates/${id}`, { method: 'DELETE' }),
  fork: (id: string, data: { name?: string; description?: string }) =>
    request<ApiResponse<TeamTemplate>>(`/templates/${id}/fork`, { method: 'POST', body: JSON.stringify(data) }),
  exportJson: (id: string) =>
    request<ApiResponse<TeamTemplate>>(`/templates/${id}/export`),
  importJson: (data: Partial<TeamTemplate>) =>
    request<ApiResponse<TeamTemplate>>('/templates/import', { method: 'POST', body: JSON.stringify(data) }),
  revisions: (id: string) =>
    request<ApiResponse<TemplateRevision[]>>(`/templates/${id}/revisions`),
  revision: (id: string, timestamp: string) =>
    request<ApiResponse<TemplateRevision>>(`/templates/${id}/revisions/${encodeURIComponent(timestamp)}`),
  publish: (id: string, data?: { name?: string; description?: string }) =>
    request<ApiResponse<TeamTemplate>>(`/templates/${id}/publish`, { method: 'POST', body: JSON.stringify(data || {}) }),
};
